/**
 * Kroger OAuth 2.0 Authentication Service
 * Handles token management using Client Credentials flow
 */

import type { KrogerAuthToken, KrogerErrorResponse } from '../types/kroger'
import { clientLogger } from '../utils/clientLogger'

interface StoredToken extends KrogerAuthToken {
  expires_at: number // Timestamp when token expires
}

class KrogerAuthService {
  private storageKey = 'kroger_auth_token'
  private token: StoredToken | null = null

  constructor() {
    this.loadStoredToken()
  }

  /**
   * Get valid access token (refresh if needed)
   */
  async getAccessToken(): Promise<string | null> {
    // Check if current token is valid
    if (this.token && this.isTokenValid(this.token)) {
      return this.token.access_token
    }

    // Token expired or doesn't exist, get new one
    const newToken = await this.requestNewToken()
    if (newToken) {
      this.storeToken(newToken)
      return newToken.access_token
    }

    return null
  }

  /**
   * Request new token using Client Credentials flow
   * NOTE: Backend handles all credentials - client just requests a token
   */
  private async requestNewToken(): Promise<KrogerAuthToken | null> {
    try {
      clientLogger.debug({}, 'Requesting new access token from backend proxy')

      // Backend handles credentials - client just makes request
      const response = await fetch('http://localhost:3005/api/kroger/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const errorData: KrogerErrorResponse = await response.json()
        clientLogger.error({ status: response.status, errors: errorData.errors }, 'Token request failed')
        return null
      }

      const tokenData: KrogerAuthToken = await response.json()
      clientLogger.debug({ token_type: tokenData.token_type, expires_in: tokenData.expires_in, scope: tokenData.scope }, 'New token received')

      return tokenData
    } catch (error) {
      clientLogger.error({ err: error instanceof Error ? error : new Error(String(error)) }, 'Error requesting token')
      return null
    }
  }

  /**
   * Check if token is valid (not expired)
   */
  private isTokenValid(token: StoredToken): boolean {
    const now = Date.now()
    const bufferTime = 5 * 60 * 1000 // 5 minutes buffer
    return now < (token.expires_at - bufferTime)
  }

  /**
   * Store token in memory and localStorage
   */
  private storeToken(tokenData: KrogerAuthToken): void {
    const expiresAt = Date.now() + (tokenData.expires_in * 1000)
    
    const storedToken: StoredToken = {
      ...tokenData,
      expires_at: expiresAt
    }

    this.token = storedToken

    // Store in localStorage (without sensitive data in production)
    try {
      const storageData = {
        expires_at: expiresAt,
        token_type: tokenData.token_type,
        scope: tokenData.scope
      }
      localStorage.setItem(this.storageKey, JSON.stringify(storageData))
    } catch (error) {
      clientLogger.warn({ err: error instanceof Error ? error : new Error(String(error)) }, 'Could not store token data')
    }
  }

  /**
   * Load stored token from localStorage
   */
  private loadStoredToken(): void {
    try {
      const stored = localStorage.getItem(this.storageKey)
      if (stored) {
        const data = JSON.parse(stored)
        // Only load metadata, not the actual token (security)
        clientLogger.debug({ expires_at: new Date(data.expires_at).toISOString(), token_type: data.token_type }, 'Found stored token metadata')
      }
    } catch (error) {
      clientLogger.warn({ err: error instanceof Error ? error : new Error(String(error)) }, 'Could not load stored token')
    }
  }

  /**
   * Clear stored token
   */
  clearToken(): void {
    this.token = null
    try {
      localStorage.removeItem(this.storageKey)
    } catch (error) {
      clientLogger.warn({ err: error instanceof Error ? error : new Error(String(error)) }, 'Could not clear stored token')
    }
  }

  /**
   * Check if authentication is configured
   * NOTE: Backend manages credentials, so we just check if backend is available
   */
  isConfigured(): boolean {
    // Always return true - backend will handle configuration validation
    return true
  }

  /**
   * Test authentication (useful for debugging)
   */
  async testAuthentication(): Promise<boolean> {
    if (!this.isConfigured()) {
      clientLogger.warn({}, 'Kroger API not configured with real credentials')
      return false
    }

    try {
      const token = await this.getAccessToken()
      return token !== null
    } catch (error) {
      clientLogger.error({ err: error instanceof Error ? error : new Error(String(error)) }, 'Authentication test failed')
      return false
    }
  }

  /**
   * Get token info for debugging
   */
  getTokenInfo(): { isValid: boolean; expiresAt: string | null; scope: string | null } {
    if (!this.token) {
      return { isValid: false, expiresAt: null, scope: null }
    }

    return {
      isValid: this.isTokenValid(this.token),
      expiresAt: new Date(this.token.expires_at).toISOString(),
      scope: this.token.scope || null
    }
  }
}

export const krogerAuthService = new KrogerAuthService()
export default krogerAuthService